/**
 * Column-presets domain — React Query hooks.
 *
 * Read hooks are pass-through re-exports of the generated client, plus
 * a small `useGetColumnPresetOptions` adapter that ports the legacy
 * `transformResponse` (raw preset list → `{ label, value, original }`
 * options) into a post-query `select` so the mapping happens inside
 * React Query and consumers don't need to remap themselves.
 *
 * Write hooks go through `useInvalidatingMutation` — `ColumnPresetsMutationId`
 * is compile-time enforced.
 */

import { useMemo } from 'react'

import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'
import {
  toColumnPreset,
  type ColumnPreset,
  type ColumnPresetOption,
} from './utils'

// ── Generated query-hook re-exports ────────────────────────────────

export {
  useListColumnPresets,
  listColumnPresetsQueryKey,
} from '../generated/hooks/columnPresetsController'

// ── Generated type re-exports ──────────────────────────────────────

export type {
  ListColumnPresetsQueryParams,
  ListColumnPresetsQueryResponse,
} from '../generated/types/ListColumnPresets'
export type {
  CreateColumnPresetMutationRequest,
  CreateColumnPresetMutationResponse,
} from '../generated/types/CreateColumnPreset'
export type {
  UpdateColumnPresetMutationRequest,
  UpdateColumnPresetMutationResponse,
  UpdateColumnPresetPathParams,
} from '../generated/types/UpdateColumnPreset'
export type { DeleteColumnPresetPathParams } from '../generated/types/DeleteColumnPreset'

// ── Internal imports for wrappers + mutation hooks ────────────────

import { useListColumnPresets as _useListColumnPresets } from '../generated/hooks/columnPresetsController'
import { createColumnPreset as createColumnPresetClient } from '../generated/clients/createColumnPreset'
import { updateColumnPreset as updateColumnPresetClient } from '../generated/clients/updateColumnPreset'
import { deleteColumnPreset as deleteColumnPresetClient } from '../generated/clients/deleteColumnPreset'

import type { FilterIdentifier } from '../../models/saved_view'
import type { CreateColumnPresetMutationRequest } from '../generated/types/CreateColumnPreset'
import type {
  UpdateColumnPresetMutationRequest,
  UpdateColumnPresetPathParams,
} from '../generated/types/UpdateColumnPreset'
import type { DeleteColumnPresetPathParams } from '../generated/types/DeleteColumnPreset'

// ── Hand-written query hooks ──────────────────────────────────────

/**
 * List column presets remapped to `{ label, value, original }` options.
 *
 * Replaces the legacy `getColumnPresetOptions` + `transformResponse`
 * pair: instead of remapping inside axios, the generated list hook
 * runs unchanged and the mapping happens in a memoized derivation
 * over its data. The legacy hook also gated the query on a truthy
 * identifier — preserved here via the generated `query.enabled` knob.
 */
export function useGetColumnPresetOptions(identifier?: FilterIdentifier) {
  const query = _useListColumnPresets(
    { identifier: identifier ?? '' },
    { query: { enabled: !!identifier } }
  )

  const data = useMemo<ColumnPresetOption[] | undefined>(() => {
    if (!query.data) return undefined
    return query.data.map((generated) => {
      const original = toColumnPreset(generated)
      return { label: original.name, value: original.id, original }
    })
  }, [query.data])

  return { ...query, data }
}

/**
 * List every column preset visible to the current user.
 *
 * Thin wrapper over the generated hook that narrows the response to
 * the augmented `ColumnPreset[]` (typed `payload` + `FilterIdentifier`
 * enum).
 */
export function useGetAllColumnPresets() {
  const query = _useListColumnPresets(undefined, {
    query: { refetchOnMount: 'always' },
  })
  const data = useMemo<ColumnPreset[] | undefined>(
    () => query.data?.map(toColumnPreset),
    [query.data]
  )
  return { ...query, data }
}

// ── Mutation hooks with auto-invalidation ─────────────────────────

export function useCreateColumnPreset() {
  return useInvalidatingMutation('createColumnPreset', invalidationRegistry, {
    mutationFn: (variables: { data: CreateColumnPresetMutationRequest }) =>
      createColumnPresetClient(variables.data),
  })
}

export function useUpdateColumnPreset() {
  return useInvalidatingMutation('updateColumnPreset', invalidationRegistry, {
    mutationFn: (variables: {
      id: UpdateColumnPresetPathParams['id']
      data: UpdateColumnPresetMutationRequest
    }) => updateColumnPresetClient({ id: variables.id }, variables.data),
  })
}

export function useDeleteColumnPreset() {
  return useInvalidatingMutation('deleteColumnPreset', invalidationRegistry, {
    mutationFn: (variables: { id: DeleteColumnPresetPathParams['id'] }) =>
      deleteColumnPresetClient({ id: variables.id }),
  })
}
